/**
 * Create / edit a class, and request a change of the school's curriculum.
 *
 * The curriculum is the school's board, fixed per school; changing it is a
 * request a superadmin approves, not an edit. Only approved teachers can be
 * made class teacher.
 */

import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { Class } from '../../types/lms';
import type { RosterPerson } from '../../hooks/useSchoolRoster';
import { createClass, updateClass } from '../../services/schoolRosterService';
import { getAvailableCurriculums, getAvailableSubjects } from '../../lib/firebase/queries/curriculumChapters';
import { createCurriculumChangeRequest } from '../../services/curriculumChangeRequestService';
import type { UserProfile } from '../../utils/rbac';

const FIELD = 'h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm text-foreground';
export const ALL_SUBJECTS = 'All Subjects';

/** "Class 7" → 7; anything else → 0. */
export const gradeOf = (name: string | undefined) => {
  const m = /class\s*(\d{1,2})\b/i.exec(name ?? '');
  const n = m ? Number(m[1]) : 0;
  return n >= 1 && n <= 12 ? n : 0;
};

export function ClassFormDialog({
  open,
  editing,
  schoolId,
  curriculum,
  teachers,
  canRequestCurriculum,
  onRequestCurriculum,
  onClose,
}: {
  open: boolean;
  /** The class being edited, or null to create one. */
  editing: Class | null;
  schoolId: string;
  curriculum: string | null;
  teachers: RosterPerson[];
  canRequestCurriculum: boolean;
  onRequestCurriculum: () => void;
  onClose: () => void;
}) {
  const [grade, setGrade] = useState(0);
  const [section, setSection] = useState('');
  const [subject, setSubject] = useState('');
  const [year, setYear] = useState('');
  const [classTeacher, setClassTeacher] = useState('');
  const [subjects, setSubjects] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGrade(gradeOf(editing?.class_name));
    setSection(editing?.section ?? '');
    setSubject(editing?.subject && editing.subject !== ALL_SUBJECTS ? editing.subject : '');
    setYear(editing?.academic_year ?? String(new Date().getFullYear()));
    setClassTeacher('');
  }, [open, editing]);

  useEffect(() => {
    if (!grade || !curriculum) {
      setSubjects([]);
      return;
    }
    let cancelled = false;
    getAvailableSubjects(curriculum, grade)
      .then((list) => !cancelled && setSubjects(list))
      .catch(() => !cancelled && setSubjects([]));
    return () => {
      cancelled = true;
    };
  }, [grade, curriculum]);

  const approved = teachers.filter((t) => t.approvalStatus === 'approved');
  // An edited class whose name is not "Class N" keeps its name unless a grade is picked.
  const className = grade ? `Class ${grade}` : editing?.class_name ?? '';

  const save = async () => {
    if (!className) {
      toast.error('Choose the class (1 to 12).');
      return;
    }
    setBusy(true);
    try {
      const fields = {
        schoolId,
        class_name: className,
        section: section.trim() || null,
        subject: subject || ALL_SUBJECTS,
        academic_year: year.trim() || null,
      };
      const result = editing
        ? await updateClass(editing.id, fields)
        : await createClass({ ...fields, class_teacher_id: classTeacher || null, curriculum });
      if (!result.ok) {
        toast.error(result.message || 'The class could not be saved.');
        return;
      }
      toast.success(editing ? 'Class updated' : `${className}${section ? ` ${section}` : ''} created`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.class_name}` : 'New class'}</DialogTitle>
          <DialogDescription>
            Curriculum: <span className="font-medium text-foreground">{curriculum || 'not set'}</span>
            {canRequestCurriculum && (
              <>
                {' · '}
                <button type="button" className="text-primary hover:underline" onClick={onRequestCurriculum}>
                  Request a change
                </button>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="cls-grade">Class</Label>
            <select id="cls-grade" className={FIELD} value={grade || ''} onChange={(e) => setGrade(Number(e.target.value) || 0)}>
              <option value="">{editing && !gradeOf(editing.class_name) ? editing.class_name : 'Choose…'}</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  Class {n}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cls-section">Section</Label>
            <Input id="cls-section" value={section} onChange={(e) => setSection(e.target.value)} placeholder="e.g. A" maxLength={10} className="h-9" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="cls-subject">Subject</Label>
            <select id="cls-subject" className={FIELD} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!grade && !editing}>
              <option value="">{ALL_SUBJECTS}</option>
              {[...new Set([...subjects, ...(subject ? [subject] : [])])].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className={editing ? 'col-span-2 space-y-1.5' : 'space-y-1.5'}>
            <Label htmlFor="cls-year">Academic year</Label>
            <Input id="cls-year" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026-27" maxLength={20} className="h-9" />
          </div>
          {!editing && (
            <div className="space-y-1.5">
              <Label htmlFor="cls-teacher">Class teacher</Label>
              <select id="cls-teacher" className={FIELD} value={classTeacher} onChange={(e) => setClassTeacher(e.target.value)}>
                <option value="">None yet</option>
                {approved.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {!editing && approved.length === 0 && (
          <p className="text-xs text-muted-foreground">Approve a teacher in the Teachers tab to make them class teacher.</p>
        )}
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !className}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {editing ? 'Save' : 'Create class'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CurriculumRequestDialog({
  open,
  profile,
  schoolId,
  current,
  onClose,
}: {
  open: boolean;
  profile: UserProfile | null;
  schoolId: string;
  current: string | null;
  onClose: () => void;
}) {
  const [options, setOptions] = useState<string[]>([]);
  const [requested, setRequested] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRequested('');
    setReason('');
    getAvailableCurriculums().then(setOptions).catch(() => setOptions([]));
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      // The service reports success and failure itself.
      if (await createCurriculumChangeRequest(profile, schoolId, requested, reason)) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request a curriculum change</DialogTitle>
          <DialogDescription>A superadmin reviews the request. Your classes keep the current curriculum until then.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cur-req">Requested curriculum</Label>
            <select id="cur-req" className={FIELD} value={requested} onChange={(e) => setRequested(e.target.value)}>
              <option value="">Choose…</option>
              {options
                .filter((c) => c.toUpperCase() !== (current ?? '').toUpperCase())
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cur-reason">Reason (optional)</Label>
            <Input id="cur-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9" maxLength={300} />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !requested}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Submit request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
